import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { AuthContext } from "../contexts/auth";
import { Link } from 'wouter';
import Swal from 'sweetalert2';
import Fetching from './fetching';
import moment from 'moment';
import DayDetailsModal from './day_details_modal';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddGiustificativoModal from './modal_add_giustificativo';
import CustomTooltipComponent from './custom_tooltip_component';
import { DocumentPlusIcon, BriefcaseIcon, FingerPrintIcon, DocumentTextIcon, InformationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import RunningWithErrorsOutlinedIcon from '@mui/icons-material/RunningWithErrorsOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { getTimbratureWeek } from '../services/api_timbrature'; // stessa API, accetta [start,end]
import { getMonthBlocked } from '../services/api_months_blocked';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';

// helpers
function buildDaysArray(startISO, endISO) {
  const days = [];
  if (!startISO || !endISO) return days;
  const start = moment(startISO, 'YYYY-MM-DD');
  const end   = moment(endISO,   'YYYY-MM-DD');
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return days;
  const cur = start.clone();
  while (cur.isSameOrBefore(end, 'day')) {
    days.push(cur.toDate());
    cur.add(1, 'day');
  }
  return days;
}
function fmtISO(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function TimbraturaMonthTable({
    page, pageSize, setTotalResult, totalResult,
    setNextPage, setPrevPage, selectedUnit,
    employeeIds = [], startDate, endDate,
}) {
    const { token, profile } = useContext(AuthContext);
    const isEmployee = profile.customer_type === 'employee';
    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');
    const hasViewShiftPermission =
        profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.view_shifts_all') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_all');

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [attendanceData, setAttendanceData] = useState(null);
    const [dipendenti, setDipendenti] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [blockedMonths, setBlockedMonths] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [modalDetailsData, setModalDetailsData] = useState();
    const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false);
    const [showModalAddGiustificativo, setShowModalAddGiustificativo] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const controller = useRef(null);

    // giorni del mese dal parent (startDate/endDate ISO)
    const days = useMemo(() => buildDaysArray(startDate, endDate), [startDate, endDate]);

    const tableContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const getData = async (signal) => {
        if (!startDate || !endDate) return;
        setIsLoading(true);
        setIsError(false);
        try {
        const attendanceResponse = await getTimbratureWeek(
            token,
            { startDate, endDate },
            selectedUnit?.id,
            employeeIds,
            page,
            pageSize,
            signal,
            'inoouts',
        );

        const arrayAttendance = Array.isArray(attendanceResponse.results)
            ? attendanceResponse.results : [];

        setNextPage(attendanceResponse.next);
        setPrevPage(attendanceResponse.previous);
        setTotalResult(attendanceResponse.count);

        const mappedAttendanceData = arrayAttendance.map((employee) => ({
            ...employee,
            days: employee.days.map((day) => ({
            date: day.date,
            has_justifications: isEmployee && !hasManageJustificationPermission ? [] : day.has_justifications,
            has_active_contract: day.has_active_contract,
            time_slots: isEmployee && !hasViewShiftPermission ? [] : day.time_slots,
            inouts: day.inouts.map((io) => ({
                ...io,
                over_time_checkin: io.over_time_checkin,
                over_time_checkout: io.over_time_checkout,
            })),
            typology: day.typology,
            })),
        }));

        setAttendanceData(mappedAttendanceData);

        setDipendenti(arrayAttendance.map((e) => ({
            first_name: e.first_name, last_name: e.last_name, cf: e.cf, id: e.id, email_auth: e.email_auth,
        })));

        // holidays
        const holidaysData = [];
        arrayAttendance.forEach((e) => {
            e.days.forEach((day) => {
            if (day.holidays?.length) {
                day.holidays.forEach((h) => holidaysData.push({ date: day.date, name: h.name }));
            }
            });
        });
        setHolidays(holidaysData);

        // mesi bloccati
        const blockedMonthsResponse = await getMonthBlocked(token, { startDate, endDate });
        const months = Array.isArray(blockedMonthsResponse)
            ? blockedMonthsResponse
            : Array.isArray(blockedMonthsResponse?.results)
            ? blockedMonthsResponse.results
            : [];
        setBlockedMonths(months);

        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            if (err?.response?.status !== 402) setIsError(true);
        } finally {
            if (!signal?.aborted) setIsLoading(false);
        }
    };

    useEffect(() => {
        controller.current?.abort();
        controller.current = new AbortController();
        const signal = controller.current.signal;
        getData(signal);
        return () => controller.current?.abort();
    }, [token, startDate, endDate, selectedUnit, employeeIds, page, pageSize]);

    const isDayBlocked = (date) =>
        blockedMonths.some(m => Number(m.year) === date.getFullYear() && Number(m.month) === (date.getMonth() + 1));

    const formatDay = (date) => {
        const localISO = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString().split('T')[0];
        const isHoliday = holidays.some(h => h.date === localISO);
        const isSunday = date.getDay() === 0;
        const options = { weekday: 'long', day: 'numeric' };
        if (isHoliday) return <div className="text-orange-500 font-bold">{date.toLocaleDateString('it-IT', options)}</div>;
        if (isSunday)  return <div className="text-emerald-500 font-bold">{date.toLocaleDateString('it-IT', options)}</div>;
        return <div className="text-gray-600">{date.toLocaleDateString('it-IT', options)}</div>;
    };

    const isWeekend = (date) => new Date(date).getDay() === 0;

    const startDragging = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - tableContainerRef.current.offsetLeft);
        setScrollLeft(tableContainerRef.current.scrollLeft);
    };
    const stopDragging = () => setIsDragging(false);
    const onDrag = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - tableContainerRef.current.offsetLeft;
        tableContainerRef.current.scrollLeft = scrollLeft - (x - startX) * 2;
    };
    useEffect(() => {
        if (isDragging) {
        document.addEventListener('mousemove', onDrag);
        document.addEventListener('mouseup', stopDragging);
        } else {
        document.removeEventListener('mousemove', onDrag);
        document.removeEventListener('mouseup', stopDragging);
        }
        return () => {
        document.removeEventListener('mousemove', onDrag);
        document.removeEventListener('mouseup', stopDragging);
        };
    }, [isDragging]); // eslint-disable-line

    const handleOpenDetailsModal = (dipendente, formattedDay) => {
        setSelectedEmployee(dipendente);
        setModalDetailsData(formattedDay);
        setIsModalDetailsOpen(true);
    };

    const handleAddGiustificativoModal = (dipendente, dayISO, attendanceForDay) => {
        setSelectedEmployee(dipendente);
        setSelectedDay(dayISO);
        setModalDetailsData(attendanceForDay);
        setShowModalAddGiustificativo(true);
    };

    const handleNewJustificationRequest = () => {
        setAttendanceData(prev =>
        prev.map((employee) => {
            if (employee.id !== selectedEmployee.id) return employee;
            return {
            ...employee,
            days: employee.days.map((d) =>
                d.date === selectedDay ? { ...d, has_justifications: true } : d
            ),
            };
        })
        );
        setShowModalAddGiustificativo(false);
    };

    const formatTimbrature = (inout, currentDate) => {
        const { checkin, checkout, over_time_checkin, over_time_checkout } = inout;
        const checkinDate = checkin ? moment(checkin) : null;
        const checkoutDate = checkout ? moment(checkout) : null;

        if (!checkinDate && !checkoutDate) {
            return 'Non presente';
        }

        const formattedCheckin = checkinDate
            ? checkinDate.isSame(moment(currentDate), 'day')
            ? checkinDate.format('HH:mm')
            : checkinDate.format('DD/MM/YYYY HH:mm')
            : <span className="text-gray-700">/</span>;

        const isNextDayMidnight =
            checkoutDate &&
            checkoutDate.format('HH:mm') === '00:00' &&
            checkoutDate.isSame(moment(currentDate).add(1, 'day'), 'day');

        const formattedCheckout = checkoutDate
            ? isNextDayMidnight
            ? '24:00'
            : checkoutDate.isSame(moment(currentDate), 'day')
                ? checkoutDate.format('HH:mm')
                : checkoutDate.format('DD/MM/YYYY HH:mm')
            : <span className="text-gray-800">/</span>;

        const checkinColor = over_time_checkin ? 'text-red-500' : 'text-green-500';
        const checkoutColor = over_time_checkout ? 'text-red-500' : 'text-green-500';

        return (
            <span>
            <span className={checkinColor}>{formattedCheckin}</span> -{' '}
            <span className={checkoutColor}>{formattedCheckout}</span>
            </span>
        );
    };

        const getTimbraturaTooltip = (inout, { isMiddleDay }) => {
        const { checkin, checkout, over_time_checkin, over_time_checkout } = inout;

        // Giorno "di mezzo" di una timbratura multi-day
        if (isMiddleDay) {
            return (
            <div className="flex items-center gap-1">
                <PendingOutlinedIcon style={{ height: 18, width: 18, color: '#6b7280' }} />
                <span>Timbratura in corso</span>
            </div>
            );
        }

        if (!checkin && !checkout) {
            return 'Nessuna timbratura';
        }

        const parts = [];

        // ---- Entrata ----
        if (checkin) {
            const InIcon = over_time_checkin
            ? RunningWithErrorsOutlinedIcon
            : CheckCircleOutlineOutlinedIcon;

            const inColor = over_time_checkin ? '#ef4444' : '#22C55E';
            const inText = over_time_checkin
            ? 'Entrata fuori orario'
            : 'Entrata in orario';

            parts.push(
            <div key="entrata" className="flex items-center gap-1">
                <InIcon style={{ height: 18, width: 18, color: inColor }} />
                <span>{inText}</span>
            </div>
            );
        }

        // ---- Uscita ----
        if (checkout) {
            const OutIcon = over_time_checkout
            ? RunningWithErrorsOutlinedIcon
            : CheckCircleOutlineOutlinedIcon;

            const outColor = over_time_checkout ? '#ef4444' : '#22C55E';
            const outText = over_time_checkout
            ? 'Uscita fuori orario'
            : 'Uscita in orario';

            parts.push(
            <div key="uscita" className="flex items-center gap-1">
                <OutIcon style={{ height: 18, width: 18, color: outColor }} />
                <span>{outText}</span>
            </div>
            );
        } else if (checkin) {
            // timbratura aperta → uscita in corso
            parts.push(
            <div key="uscita-in-corso" className="flex items-center gap-1">
                <PendingOutlinedIcon style={{ height: 18, width: 18, color: '#f97316' }} />
                <span>Uscita in corso</span>
            </div>
            );
        }

        // se per qualche motivo non c'è nulla, fallback
        if (!parts.length) {
            return 'Nessuna timbratura';
        }

        // Tooltip può restituire un ReactNode: raggruppo tutto in un contenitore
        return (
            <div className="flex flex-col gap-0.5">
                {parts}
            </div>
        );
    };

    const refreshEmployeeRange = async (employeeId) => {
        if (!employeeId || !startDate || !endDate) return;
        try {
        const res = await getTimbratureWeek(
            token,
            { startDate, endDate },
            selectedUnit?.id,
            [employeeId],
            1,
            pageSize,
            null,
            'inoouts'
        );
        const one = Array.isArray(res.results) ? res.results[0] : null;
        if (!one) return;
        const normalized = {
            ...one,
            days: one.days.map((day) => ({
            date: day.date,
            has_justifications: (profile.customer_type === 'employee' && !hasManageJustificationPermission) ? [] : day.has_justifications,
            has_active_contract: day.has_active_contract,
            time_slots: (profile.customer_type === 'employee' && !hasViewShiftPermission) ? [] : day.time_slots,
            inouts: day.inouts.map((io) => ({
                ...io,
                over_time_checkin: io.over_time_checkin,
                over_time_checkout: io.over_time_checkout,
            })),
            typology: day.typology,
            })),
        };
        setAttendanceData(prev => {
            if (!Array.isArray(prev)) return [normalized];
            const idx = prev.findIndex(e => e.id === normalized.id);
            if (idx === -1) return [...prev, normalized];
            const copy = [...prev];
            copy[idx] = normalized;
            return copy;
        });
        } catch (e) {
        console.error('refreshEmployeeRange error', e);
        }
    };

    if(isLoading)
        return (
            <div className='w-full p-10 bg-gray-100'>
                <Fetching />
            </div>
        );
    
    const MIN_EMP_COL = 128;
    const MIN_DAY_COL = 128;
    const MIN_ICON_COL = 60;
    const minTotalWidth =
    MIN_DAY_COL + MIN_ICON_COL + dipendenti.length * MIN_EMP_COL;

    return (
        <div>
            <div className="overflow-x-auto" style={{ overflowY: 'scroll', overflowX: 'auto', height: '100%' }}>
                <div
                ref={tableContainerRef}
                style={{
                    overflowX: 'auto', overflowY: 'auto',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    maxHeight: '70vh', position: 'relative'
                }}
                onMouseDown={startDragging}
                >
                {isError ? (
                    <div className="m-4 relative block rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                    <span className="mt-2 block text-sm font-semibold text-gray-900">Errore nel caricamento dei dati</span>
                    </div>
                ) : (
                    <table 
                        className="table-fixed border divide-y divide-gray-200" 
                        style={{
                            width: '100%',
                            tableLayout: 'fixed',
                            minWidth: minTotalWidth
                        }} 
                    >
                        <colgroup>
                            <col style={{ width: 128 }} />
                            <col style={{ width: 60  }} />
                            {dipendenti.map((_, i) => (
                                <col key={i} style={{ width: 'auto' }} />
                            ))}
                        </colgroup>

                        <thead style={{ position: 'sticky', top: 0, zIndex: 11, background: 'white' }}>
                            <tr>
                            {/* Giorno */}
                            <th
                                className="text-left text-xs font-semibold text-gray-600 uppercase p-2 py-4"
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    left: 0,
                                    backgroundColor: 'white',
                                    zIndex: 50,
                                    width: 128,
                                    minWidth: 128,
                                    maxWidth: 128,
                                    boxShadow: 'inset -1px 0 #d1d5db, inset 1px 0 #d1d5db, inset 0 -1px #d1d5db, inset 0 1px #d1d5db',
                                }}
                            >
                                Giorno
                            </th>

                            {/* colonna icone riga */}
                            <th
                                className="text-left text-xs font-semibold text-gray-600 uppercase pl-2 w-[60px]"
                                style={{
                                position: 'sticky',
                                top: 0,
                                left: '128px',
                                backgroundColor: 'white',
                                zIndex: 45,
                                minWidth: '60px',
                                maxWidth: '60px',
                                boxShadow:
                                    'inset -1px 0 #d1d5db, inset 1px 0 #d1d5db, inset 0 -1px #d1d5db, inset 0 1px #d1d5db',
                                }}
                            />

                            {/* intestazioni dipendenti */}
                            {dipendenti.map((d) => (
                                <th
                                key={d.id}
                                className="text-center text-xs font-semibold text-gray-600 uppercase p-2"
                                style={{
                                    backgroundColor: 'white',
                                    minWidth: '128px',
                                    boxShadow: 'inset -1px 0 #d1d5db, inset 1px 0 #d1d5db, inset 0 -1px #d1d5db, inset 0 1px #d1d5db',
                                    top: 0,
                                    position: 'sticky',
                                    zIndex: 40, 
                                }}
                                >
                                <Link to={`/dipendenti/${d.cf}`} className="hover:text-indigo-700">
                                    {d.first_name ? `${d.last_name} ${d.first_name}`.slice(0, 25) : d.cf}
                                    {d.first_name && `${d.last_name} ${d.first_name}`.length > 25 ? '...' : ''}
                                </Link>
                                </th>
                            ))}
                            </tr>
                        </thead>

                        <tbody>
                            {days.map((day, dayIndex) => {
                            const dayISO = fmtISO(day);
                            return ['Turno', 'Timbrature', 'Giustificativi', 'Info'].map((tipo, tipoIndex) => (
                                <tr
                                key={`${dayISO}-${tipoIndex}`}
                                className={dayIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                >
                                {/* colonna Giorno (sticky) */}
                                {tipoIndex === 0 && (
                                    <td
                                    rowSpan={4}
                                    className="sticky left-0 z-10 text-xs font-semibold text-gray-600 uppercase pl-2 bg-inherit whitespace-nowrap overflow-hidden text-ellipsis"
                                    style={{
                                        width: 128, minWidth: 128, maxWidth: 128,
                                        boxShadow:'inset -1px 0 #d1d5db, inset 1px 0 #d1d5db, inset 0 -1px #d1d5db, inset 0 1px #d1d5db' 
                                    }}
                                    >
                                    {formatDay(day)}
                                    </td>
                                )}

                                {/* colonna icona riga (sticky) */}
                                <td
                                    className="sticky left-[128px] z-10 text-xs font-medium text-gray-700 bg-inherit whitespace-nowrap overflow-hidden text-ellipsis w-[60px]"
                                    style={{ 
                                        minWidth:'60px', 
                                        maxWidth:'60px', 
                                        boxShadow:'inset -1px 0 #d1d5db, inset 1px 0 #d1d5db, inset 0 -1px #d1d5db, inset 0 1px #d1d5db'
                                    }}
                                >
                                    <div className="flex items-center justify-center h-full">
                                        {tipo === 'Turno' && (
                                        <CustomTooltipComponent title="Turno" placement="left" arrow>
                                            <div>
                                                <BriefcaseIcon className="h-5 w-5 text-indigo-800" />
                                            </div>
                                        </CustomTooltipComponent>
                                        )}
                                        {tipo === 'Timbrature' && (
                                        <CustomTooltipComponent title="Timbrature" placement="left" arrow>
                                            <div>
                                                <FingerPrintIcon className="h-5 w-5 text-indigo-800" />
                                            </div>
                                        </CustomTooltipComponent>
                                        )}
                                        {tipo === 'Giustificativi' && (
                                        <CustomTooltipComponent title="Giustificativi" placement="left" arrow>
                                            <div>
                                                <DocumentTextIcon className="h-5 w-5 text-indigo-800" />
                                            </div>
                                        </CustomTooltipComponent>
                                        )}
                                        {tipo === 'Info' && (
                                        <CustomTooltipComponent title="Info" placement="left" arrow>
                                            <div>
                                                <InformationCircleIcon className="h-5 w-5 text-indigo-800" />
                                            </div>
                                        </CustomTooltipComponent>
                                        )}
                                    </div>
                                </td>

                                {/* celle dipendenti */}
                                {dipendenti.map((dip) => {
                                    const employeeAttendance =
                                    attendanceData?.find((e) => e.id === dip.id)?.days || [];
                                    const attendanceForDay = employeeAttendance.find((a) => a.date === dayISO);
                                    const hasActiveContract = attendanceForDay?.has_active_contract;
                                    const timeSlots = attendanceForDay?.time_slots || [];
                                    const timbrature = attendanceForDay?.inouts || [];
                                    const isHoliday = holidays.some((h) => h.date === dayISO);
                                    const weekend = isWeekend(day);

                                    const canViewDetails =
                                    profile?.customer_type === 'employee'
                                        ? profile.employee?.permissions?.some((p) =>
                                            [
                                            'hr.view_shifts_ou',
                                            'hr.view_shifts_all',
                                            'hr.manage_shifts_ou',
                                            'hr.manage_shifts_all',
                                            'hr.view_inouts_ou',
                                            'hr.view_inouts_all',
                                            'hr.manage_inouts_ou',
                                            'hr.manage_inouts_all',
                                            ].includes(p)
                                        )
                                        : true;

                                    const canManageJR =
                                    profile?.customer_type === 'employee'
                                        ? profile.employee?.permissions?.some((p) =>
                                            ['hr.manage_justification_ou', 'hr.manage_justification_all'].includes(p)
                                        )
                                        : true;

                                    const handleClick = () => {
                                    if (!canViewDetails)
                                        return Swal.fire({ title: 'Non hai i permessi per eseguire questa azione', icon: 'warning', confirmButtonText: 'Chiudi' });
                                    if (!hasActiveContract)
                                        return Swal.fire({ title: 'Contratto non attivo sul giorno selezionato', icon: 'warning', confirmButtonText: 'Chiudi' });
                                    attendanceForDay && handleOpenDetailsModal(dip, dayISO);
                                    };

                                    const handleAddG = () => {
                                    if (!canManageJR)
                                        return Swal.fire({ title: 'Non hai i permessi per eseguire questa azione', icon: 'warning', confirmButtonText: 'Chiudi' });
                                    if (!hasActiveContract)
                                        return Swal.fire({ title: 'Contratto non attivo sul giorno selezionato', icon: 'warning', confirmButtonText: 'Chiudi' });
                                    attendanceForDay && handleAddGiustificativoModal(dip, dayISO, attendanceForDay);
                                    };

                                    const cellStyles = {
                                    position: 'relative',
                                    minWidth: 128,
                                    ...(!hasActiveContract ? PatternInactiveEmployee() : {}),
                                    ...(hasActiveContract && weekend ? SvgPattern() : {}),
                                    ...(hasActiveContract && isHoliday ? SvgHoliday() : {}),
                                    ...(hasActiveContract && isHoliday && weekend ? SvgHolidayWeekend() : {}),
                                    };

                                    if (!hasActiveContract) {
                                    return (
                                        <td
                                        key={`${dip.id}-${dayISO}-${tipoIndex}`}
                                        className="employee-col min-w-[128px] align-top border-2 border-gray-200 text-center text-sm font-semibold uppercase p-1.5"
                                        style={cellStyles}
                                        >
                                        <span className="text-transparent">-</span>
                                        </td>
                                    );
                                    }

                                    return (
                                    <td
                                        key={`${dip.id}-${dayISO}-${tipoIndex}`}
                                        className='employee-col min-w-[128px] align-top border-2 border-gray-200 text-center text-sm font-semibold uppercase'
                                        style={cellStyles}
                                    >
                                    {tipo === 'Turno' && (
                                        timeSlots.length > 0 ? (
                                            <ul className="text-xs p-1.5">
                                            {timeSlots
                                                .sort((a, b) => a.start_at.localeCompare(b.start_at))
                                                .map((slot, index) => (
                                                <li
                                                    key={index}
                                                    className="flex flex-col items-center justify-center text-gray-700 normal-case text-xs"
                                                >
                                                    {/* intervallo principale */}
                                                    <div className="flex-1 text-center justify-center">
                                                    <span>{slot.start_at} - {slot.end_at}</span>
                                                    </div>

                                                    {/* eventuali break */}
                                                    {Array.isArray(slot.breaks) && slot.breaks.length > 0 && (
                                                    <div className="mt-0.1 mb-1 text-[10px] text-gray-500 text-center">
                                                        {slot.breaks
                                                        .filter(b => b.is_visible !== false)
                                                        .map((br, i) => (
                                                            <span key={br.id ?? i}>
                                                            Pausa {br.start_at} - {br.end_at}
                                                            {i < slot.breaks.length - 1 && ', '}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    )}
                                                </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="flex items-center justify-center text-gray-400 normal-case text-xs p-1.5">
                                            <div className="flex-1 text-center justify-center">
                                                {attendanceForDay?.typology === 'fisso' && isHoliday
                                                ? <span className="text-gray-800">Festivo</span>
                                                : <span>Non presente</span>}
                                            </div>
                                            </div>
                                        )
                                        )}

                                        {tipo === 'Timbrature' && (
                                            timbrature.length > 0 ? (
                                                <ul className="text-xs p-1.5 w-full min-w-0 whitespace-normal break-words">
                                                    {[...timbrature]
                                                        .sort((a, b) => {
                                                            // ordino in base al checkin, se manca uso il checkout
                                                            const aDate = a.checkin || a.checkout;
                                                            const bDate = b.checkin || b.checkout;

                                                            if (!aDate && !bDate) return 0;
                                                            if (!aDate) return 1;
                                                            if (!bDate) return -1;

                                                            return new Date(aDate) - new Date(bDate);
                                                        })
                                                        .map((inout, index) => {
                                                            const ci = inout.checkin ? moment(inout.checkin) : null;
                                                            const co = inout.checkout ? moment(inout.checkout) : null;
                                                            const dayStart = moment(day).startOf('day');

                                                            const ciDay = ci && ci.clone().startOf('day');
                                                            const coDay = co && co.clone().startOf('day');

                                                            const isMultiDay = ci && co && !ciDay.isSame(coDay);
                                                            const isFirstDay = isMultiDay && ciDay.isSame(dayStart);
                                                            const isLastDay = isMultiDay && coDay.isSame(dayStart);
                                                            const isMiddleDay =
                                                                isMultiDay &&
                                                                dayStart.isAfter(ciDay) &&
                                                                dayStart.isBefore(coDay);

                                                            const durationMinutes = inout.duration_minutes;

                                                            const showDuration = durationMinutes != null && (!isMultiDay || isLastDay);

                                                            const totalMinutes = Math.round(durationMinutes);
                                                            const hours = Math.floor(totalMinutes / 60);
                                                            const minutes = totalMinutes % 60;

                                                            const formattedDuration = showDuration
                                                                ? `${hours} ${hours === 1 ? 'ora' : 'ore'}, ${String(minutes).padStart(2, '0')} min`
                                                                : null;

                                                            // colori per testo
                                                            const checkinTextClass = inout.over_time_checkin ? 'text-red-500' : 'text-green-500';
                                                            const checkoutTextClass = inout.over_time_checkout ? 'text-red-500' : 'text-green-500';

                                                            // colori per icone
                                                            const loginColor = inout.over_time_checkin ? '#ef4444' : '#22C55E';
                                                            const logoutColor = inout.over_time_checkout ? '#ef4444' : '#22C55E';

                                                            const tooltipTitle = getTimbraturaTooltip(inout, { isMiddleDay });

                                                            // icona di pending solo se non è multi-day middle e non c’è checkout
                                                            const showPendingIcon = !isMiddleDay && !inout.checkout;

                                                            return (
                                                                <li
                                                                    key={inout.id ?? index}
                                                                    className="flex flex-col items-center justify-center gap-1 w-full min-w-0 text-gray-600 border-b border-gray-200 pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
                                                                >
                                                                    {/* Prima riga: orari di ingresso e uscita */}
                                                                    <CustomTooltipComponent
                                                                        title={tooltipTitle}
                                                                        placement="top-start"
                                                                        arrow
                                                                    >
                                                                        <div className="flex flex-wrap items-center justify-center gap-1 w-full min-w-0 whitespace-normal break-words">
                                                                            {isMiddleDay ? (
                                                                                // Giorno in mezzo: solo simbolo di continuità
                                                                                <>
                                                                                    <PendingOutlinedIcon
                                                                                        style={{ height: 18, width: 18, color: '#6b7280' }}
                                                                                    />
                                                                                    <span>···</span>
                                                                                </>
                                                                            ) : isFirstDay ? (
                                                                                // PRIMO giorno di timbratura multi-day:
                                                                                // solo orario di ingresso + icona ingresso, niente icona uscita
                                                                                <>
                                                                                    {ci && (
                                                                                        <LoginIcon
                                                                                            style={{ height: 18, width: 18, color: loginColor }}
                                                                                        />
                                                                                    )}
                                                                                    {ci && (
                                                                                        <>
                                                                                            <span className={checkinTextClass}>
                                                                                                {ci.format('HH:mm')}
                                                                                            </span>
                                                                                            <span> →</span>
                                                                                        </>
                                                                                    )}
                                                                                </>
                                                                            ) : isLastDay ? (
                                                                                // ULTIMO giorno di timbratura multi-day:
                                                                                // solo orario di uscita + icona uscita, niente icona ingresso
                                                                                <>
                                                                                    {co && (
                                                                                        <>
                                                                                            <span>→ </span>
                                                                                            <span className={checkoutTextClass}>
                                                                                                {co.format('HH:mm')}
                                                                                            </span>
                                                                                        </>
                                                                                    )}
                                                                                    {co && (
                                                                                        <LogoutIcon
                                                                                            style={{ height: 18, width: 18, color: logoutColor }}
                                                                                        />
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                // Caso "normale" (stesso giorno o timbratura aperta)
                                                                                <>
                                                                                    {/* icona ingresso se c'è checkin */}
                                                                                    {ci && (
                                                                                        <LoginIcon
                                                                                            style={{ height: 18, width: 18, color: loginColor }}
                                                                                        />
                                                                                    )}

                                                                                    {/* testo standard (usa già verde/rosso per checkin/checkout) */}
                                                                                    <span>{formatTimbrature(inout, day)}</span>

                                                                                    {/* icona uscita se c'è checkout */}
                                                                                    {co && (
                                                                                        <LogoutIcon
                                                                                            style={{ height: 18, width: 18, color: logoutColor }}
                                                                                        />
                                                                                    )}

                                                                                    {/* se non c'è checkout, mostro pending */}
                                                                                    {showPendingIcon && (
                                                                                        <PendingOutlinedIcon
                                                                                            style={{ height: 18, width: 18, color: '#f97316' }}
                                                                                        />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </CustomTooltipComponent>

                                                                    {/* Seconda riga: durata della timbratura */}
                                                                    {showDuration && (
                                                                        <CustomTooltipComponent title="Durata complessiva della timbratura" placement="top" arrow>
                                                                            <span className="normal-case bg-indigo-50 rounded-md px-2 inline-flex items-center justify-center text-[11px] font-semibold text-indigo-700 whitespace-nowrap">
                                                                                <ClockIcon className="h-3 w-3 shrink-0 text-indigo-800 mr-1" />
                                                                                {formattedDuration}
                                                                            </span>
                                                                        </CustomTooltipComponent>
                                                                    )}
                                                                </li>
                                                            );
                                                        })}
                                                </ul>
                                            ) : (
                                                <div className="flex items-center justify-center text-gray-400 normal-case text-xs p-1.5">
                                                    <div className="flex-1 text-center justify-center">
                                                        <span>
                                                            {moment(day).isAfter(moment(), 'day') ? (
                                                                <span className="text-transparent">-</span>
                                                            ) : (
                                                                'Non presente'
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        )}

                                        {tipo === 'Giustificativi' && (
                                        isEmployee && !hasManageJustificationPermission
                                            ? <span className="text-transparent">-</span>
                                            : (attendanceForDay?.has_justifications !== undefined
                                                ? (
                                                <div className="flex items-center justify-between text-xs text-gray-400 p-1.5">
                                                    {attendanceForDay?.has_justifications
                                                    ? <span className="flex-1 text-center text-gray-800">SI</span>
                                                    : <span className="normal-case flex-1 text-center">Non presenti</span>}
                                                </div>
                                                )
                                                : (
                                                <div className="flex items-center justify-between text-xs text-gray-400 p-1.5">
                                                    <div className="flex-1 text-center normal-case"><span>Non presente</span></div>
                                                </div>
                                                )
                                            )
                                        )}

                                        {tipo === 'Info' && (
                                        <div className="flex flex-row space-x-2">
                                            <CustomTooltipComponent title="Visualizza dettagli" placement="bottom" arrow>
                                            <div className="flex items-center justify-center w-full h-full" onClick={handleClick}>
                                                <div className="flex items-center justify-center rounded-md bg-white p-1 shadow-sm text-indigo-800 w-full cursor-pointer hover:bg-indigo-500 hover:text-white">
                                                <ManageSearchIcon style={{ fontSize: 20 }} />
                                                </div>
                                            </div>
                                            </CustomTooltipComponent>

                                            <CustomTooltipComponent title="Aggiungi giustificativo" placement="bottom" arrow>
                                            <div className="flex items-center justify-center w-full h-full" onClick={handleAddG}>
                                                <div className="flex items-center justify-center rounded-md bg-white p-1 shadow-sm text-indigo-800 w-full cursor-pointer hover:bg-indigo-500 hover:text-white">
                                                <DocumentPlusIcon className="h-5 w-5" />
                                                </div>
                                            </div>
                                            </CustomTooltipComponent>
                                        </div>
                                        )}
                                    </td>
                                    );
                                })}
                                </tr>
                            ));
                            })}
                        </tbody>
                    </table>
                )}
                </div>
            </div>

            {showModalAddGiustificativo && (
                <AddGiustificativoModal
                    closeModalAddGiustificativi={() => setShowModalAddGiustificativo(false)}
                    dipendente={selectedEmployee}
                    formattedDay={selectedDay}
                    selectedJustification={modalDetailsData}
                    onSaveJustificationRequest={handleNewJustificationRequest}
                />
            )}

            <DayDetailsModal
                isOpen={isModalDetailsOpen}
                onClose={() => setIsModalDetailsOpen(false)}
                formattedDay={modalDetailsData}
                employee={selectedEmployee}
                onOpenAddJustification={(emp, day) => {
                setSelectedEmployee(emp);
                setSelectedDay(day);
                setModalDetailsData(null);
                setShowModalAddGiustificativo(true);
                setIsModalDetailsOpen(false);
                }}
                onAfterSaveTimbratura={() => {
                    if (selectedEmployee?.id) {
                        refreshEmployeeRange(selectedEmployee.id);
                    }
                }}
                onAfterDeleteTimbratura={() => {
                    if (selectedEmployee?.id) {
                        refreshEmployeeRange(selectedEmployee.id);
                    }
                }}
                onAfterSaveOrDeleteJustification={(hasJustifications) => {
                setAttendanceData((prev) =>
                    prev.map((employee) => {
                    if (employee.id !== selectedEmployee.id) return employee;
                    return {
                        ...employee,
                        days: employee.days.map((d) => d.date === modalDetailsData ? { ...d, has_justifications: hasJustifications } : d),
                    };
                    })
                );
                }}
                isMonthOpen={modalDetailsData ? !isDayBlocked(new Date(modalDetailsData)) : true}
                onShiftsChanged={() => selectedEmployee?.id && refreshEmployeeRange(selectedEmployee.id)}
                holidayToShow={[
                    ...new Map(
                        holidays
                        .filter(h => h.date === modalDetailsData)
                        .map(h => [h.name, h])
                    ).values()
                ]}
            />
        </div>
    );
}



const SvgHoliday = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fed7aa' fill-opacity='0.3'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};


const SvgHolidayWeekend = () => {
    return {
        backgroundColor: '#feead2',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const SvgPattern = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.1'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const PatternInactiveEmployee = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='0.3' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};